/**
 * Apex Harmony Stethoscope - Color Coordination System (with fallback images)
 * Performance-optimized version to reduce main thread work
 */

window.apexColorManager = window.apexColorManager || {};

// Performance utility functions
function throttle(fn, delay) {
  let lastCall = 0;
  return function(...args) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      return fn.apply(this, args);
    }
  };
}

document.addEventListener('DOMContentLoaded', function () {
  console.log('🔄 color-coordination.js loaded');
  
  // Detect if mobile for optimizations
  const isMobile = window.innerWidth < 750;

  // DOM cache to avoid repeated queries
  const domCache = {
    root: document.documentElement,
    carousel: null,
    slides: null,
    productButtons: null,
    featureTitles: null,
    featureLabels: null,
    colorContents: null,
    heroImages: null,
    productFeatures: null,
    footerArtworks: null,
    nextButton: null,
    prevButton: null,
    colorDescription: null,
    blogItems: null,
    footerInfo: null,
    footerSocialIcons: null
  };

  // Initialize DOM cache - don't cache elements that might not exist yet
  function cacheDOM() {
    domCache.carousel = document.getElementById('featured-product-carousel');
    domCache.slides = Array.from(document.querySelectorAll('.splide__slide'));
    domCache.productButtons = Array.from(document.querySelectorAll('.product-cta-button'));
    domCache.featureTitles = Array.from(document.querySelectorAll('.feature-title'));
    domCache.featureLabels = Array.from(document.querySelectorAll('.feature-image-label'));
    domCache.colorContents = Array.from(document.querySelectorAll('.color-specific-content'));
    domCache.heroImages = Array.from(document.querySelectorAll('.hero-image'));
    domCache.productFeatures = Array.from(document.querySelectorAll('.product-feature-image'));
    domCache.footerArtworks = Array.from(document.querySelectorAll('.footer-artwork'));
    domCache.nextButton = document.querySelector('.next-splide');
    domCache.prevButton = document.querySelector('.prev-splide');
    domCache.colorDescription = document.getElementById('color-specific-description');
    domCache.blogItems = Array.from(document.querySelectorAll('.featured-posts-title, .blog-post-title, .blog-post-meta, .blog-read-more'));
    domCache.footerInfo = document.querySelector('.footer-info');
    domCache.footerSocialIcons = Array.from(document.querySelectorAll('.footer-info__social-icon svg, .footer-info__column-title, .footer-info__newsletter-title'));
  }

  // Primary color mapping
  apexColorManager.colorMap = {
    'skyblue':       '#2B9CD6',
    'coralbloom':    '#FF4F5E',
    'gladegreen':    '#2E7D32',
    'midnightindigo':'#2C3EAB',
    'knockoutpink':  '#D81B60',
    'endlessorange': '#FE3501',
    'tidalteal':     '#00796B',
    'timelessblack': '#111111'
  };

  // Secondary colors mapping
  apexColorManager.secondaryColorMap = {
    'skyblue':       '#2691C7',
    'coralbloom':    '#E64754',
    'gladegreen':    '#256A2B',
    'midnightindigo':'#253591',
    'knockoutpink':  '#B61751',
    'endlessorange': '#D82D01',
    'tidalteal':     '#00665A',
    'timelessblack': '#333333'
  };

  // Color descriptions
  apexColorManager.colorDescriptions = {
    skyblue:       'Calming blue reminiscent of clear skies, perfect for pediatric settings.',
    coralbloom:    'Warm and inviting coral hue that adds personality to your medical attire.',
    gladegreen:    'Vibrant green that symbolizes health and vitality.',
    midnightindigo:'Deep, sophisticated indigo for a professional appearance.',
    knockoutpink:  'Bold pink that makes a statement in any medical environment.',
    endlessorange: 'Energetic orange that brings warmth to patient interactions.',
    tidalteal:     'Serene teal that combines professionalism with personality.',
    timelessblack: 'Classic black for a timeless, professional look that matches everything.'
  };

  // Product URLs for each color
  apexColorManager.productURLs = {
    'skyblue':       '/products/harmony-stethoscope-sky-blue',
    'coralbloom':    '/products/harmony-stethoscope-coral-bloom',
    'gladegreen':    '/products/harmony-stethoscope-glade-green',
    'midnightindigo':'/products/harmony-stethoscope-midnight-indigo',
    'knockoutpink':  '/products/harmony-stethoscope-knockout-pink',
    'endlessorange': '/products/harmony-stethoscope-endless-orange',
    'tidalteal':     '/products/harmony-stethoscope-tidal-teal',
    'timelessblack': '/products/harmony-stethoscope-timeless-black'
  };

  // Display names for buttons
  apexColorManager.colorDisplayNames = {
    'skyblue':       'Sky Blue',
    'coralbloom':    'Coral Bloom',
    'gladegreen':    'Glade Green',
    'midnightindigo':'Midnight Indigo',
    'knockoutpink':  'Knockout Pink',
    'endlessorange': 'Endless Orange',
    'tidalteal':     'Tidal Teal',
    'timelessblack': 'Timeless Black'
  };

  // Get color key from slide element
  function getColorKeyFromSlide(slide) {
    if (!slide) return 'skyblue';
    
    const colorLabel = slide.querySelector('.carousel-label');
    if (!colorLabel) return 'skyblue';
    
    return colorLabel.textContent.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  // Get color key from active slide
  function getActiveColorKey() {
    const activeSlide = document.querySelector('.splide__slide.active-color');
    return getColorKeyFromSlide(activeSlide);
  }

  // Update the theme class on root element
  function updateColorClass(colorKey) {
    // Use a single classList operation using DOMTokenList.replace if available
    if (domCache.root.classList.replace) {
      // Find existing color class
      const currentColorClass = Array.from(domCache.root.classList).find(c => c.startsWith('color-'));
      if (currentColorClass) {
        domCache.root.classList.replace(currentColorClass, `color-${colorKey}`);
      } else {
        domCache.root.classList.add(`color-${colorKey}`);
      }
    } else {
      // Fallback for older browsers
      domCache.root.classList.forEach(className => {
        if (className.startsWith('color-')) {
          domCache.root.classList.remove(className);
        }
      });
      domCache.root.classList.add(`color-${colorKey}`);
    }
  }

  // Update the overall color scheme
  function updateColorScheme() {
    const colorKey = getActiveColorKey();
    const primaryColor = apexColorManager.colorMap[colorKey] || '#2B9CD6';
    const secondaryColor = apexColorManager.secondaryColorMap[colorKey] || '#2691C7';
    
    // Update CSS class - let CSS variables be handled by the stylesheet
    updateColorClass(colorKey);
    
    // Update BOTH the root level CSS variables AND the --primary variable
    // This is critical for elements that use --primary vs --primary-color
    domCache.root.style.setProperty('--primary', primaryColor);
    domCache.root.style.setProperty('--secondary', secondaryColor);
    domCache.root.style.setProperty('--primary-color', primaryColor);
    
    // Call update functions on each area
    if (typeof apexColorManager.changeHeroColor === 'function') {
      apexColorManager.changeHeroColor(colorKey);
    }
    
    if (typeof apexColorManager.updateMultirowImages === 'function') {
      apexColorManager.updateMultirowImages(colorKey);
    }
    
    if (typeof apexColorManager.updateFooterArtwork === 'function') {
      apexColorManager.updateFooterArtwork(colorKey);
    }
    
    updateFeatureElements(primaryColor, secondaryColor);
    updateColorSpecificContent(colorKey);
    updateCTAButton(colorKey);
    updateMultirowColors(colorKey, primaryColor);
    updateBlogElements(primaryColor);
    updateFooterElements(primaryColor);
    
    // Dispatch an event for other scripts to listen to
    document.dispatchEvent(new CustomEvent('apex:colorSchemeUpdated', {
      detail: { 
        colorKey: colorKey,
        primaryColor: primaryColor,
        secondaryColor: secondaryColor
      }
    }));
  }

  // Update blog elements with the new color
  function updateBlogElements(primaryColor) {
    // Get fresh elements each time
    const blogTitles = document.querySelectorAll('.featured-posts-title, .blog-post-title');
    const blogMeta = document.querySelectorAll('.blog-post-meta');
    const blogReadMore = document.querySelectorAll('.blog-read-more');
    
    requestAnimationFrame(() => {
      // Update blog titles
      if (blogTitles && blogTitles.length) {
        blogTitles.forEach(title => {
          title.style.setProperty('color', primaryColor, 'important');
        });
      }
      
      // Update blog meta info
      if (blogMeta && blogMeta.length) {
        blogMeta.forEach(meta => {
          meta.style.setProperty('border-color', primaryColor, 'important');
        });
      }
      
      // Update read more links
      if (blogReadMore && blogReadMore.length) {
        blogReadMore.forEach(link => {
          link.style.setProperty('color', primaryColor, 'important');
        });
      }
    });
  }

  // Update footer elements with the new color
  function updateFooterElements(primaryColor) {
    // Get footer elements
    const footerInfo = document.querySelector('.footer-info');
    const footerTitles = document.querySelectorAll('.footer-info__column-title, .footer-info__newsletter-title');
    const footerSocialIcons = document.querySelectorAll('.footer-info__social-icon svg');
    const footerLinks = document.querySelectorAll('.footer-info a');
    
    requestAnimationFrame(() => {
      // Set the color variable on the footer container
      if (footerInfo) {
        footerInfo.style.setProperty('--primary-color', primaryColor, 'important');
      }
      
      // Update footer titles
      if (footerTitles && footerTitles.length) {
        footerTitles.forEach(title => {
          // The ::after pseudo-element gets its color from the variable
          title.style.setProperty('--primary-color', primaryColor, 'important');
        });
      }
      
      // Update social icons
      if (footerSocialIcons && footerSocialIcons.length) {
        footerSocialIcons.forEach(icon => {
          icon.style.setProperty('fill', primaryColor, 'important');
        });
      }
      
      // Update footer links hover color
      if (footerLinks && footerLinks.length) {
        footerLinks.forEach(link => {
          link.style.setProperty('--primary-color', primaryColor, 'important');
        });
      }
    });
  }

  // Function to update dynamic CTA buttons
  function updateCTAButton(colorKey) {
    // Get the button fresh each time to avoid caching issues
    const ctaButton = document.getElementById('dynamic-cta-button');
    
    if (!ctaButton) return;
    
    // Batch DOM updates with requestAnimationFrame
    requestAnimationFrame(() => {
      // Update the button text
      const displayName = apexColorManager.colorDisplayNames[colorKey] || 'Sky Blue';
      ctaButton.textContent = 'VIEW ' + displayName.toUpperCase();
      
      // Update the button href
      const productURL = apexColorManager.productURLs[colorKey] || '/products/apex-harmony-skyblue';
      ctaButton.setAttribute('href', productURL);
      
      // Update button color
      const primaryColor = apexColorManager.colorMap[colorKey] || '#2B9CD6';
      ctaButton.style.backgroundColor = primaryColor;
      ctaButton.style.borderColor = primaryColor;
    });
  }


  // Update multirow section colors (eyebrow and button)
  function updateMultirowColors(colorKey, primaryColor) {
    requestAnimationFrame(() => {
      // Update multirow section
      const multirow = document.querySelector('.multirow');
      if (multirow) {
        // Update section class
        Object.keys(apexColorManager.colorMap).forEach(color => {
          multirow.classList.remove(`color-${color}`);
        });
        multirow.classList.add(`color-${colorKey}`);
        
        // Set the primary-color variable on the multirow section
        multirow.style.setProperty('--primary-color', primaryColor, 'important');
        
        // Explicitly set background to transparent to override any inherited styles
        multirow.style.setProperty('background-color', 'transparent', 'important');
      }
      
      // Update caption/eyebrow color
      const captions = document.querySelectorAll('.image-with-text__text--caption');
      if (captions && captions.length) {
        captions.forEach(caption => {
          caption.style.setProperty('color', primaryColor, 'important');
        });
      }
      
      // Update button colors in multirow section
      const multirowButtons = document.querySelectorAll('.multirow .product-cta-button');
      if (multirowButtons && multirowButtons.length) {
        multirowButtons.forEach(btn => {
          btn.style.setProperty('color', primaryColor, 'important');
        });
      }
    });
  }
  // Update UI elements (buttons, titles, etc.)
  function updateFeatureElements(primary, secondary) {
    // Use requestAnimationFrame to batch DOM updates
    requestAnimationFrame(() => {
      // Update buttons
      if (domCache.productButtons && domCache.productButtons.length) {
        domCache.productButtons.forEach(btn => {
          btn.style.backgroundColor = primary;
          btn.style.borderColor = primary;
        });
      }
      
      // Update titles
      if (domCache.featureTitles && domCache.featureTitles.length) {
        domCache.featureTitles.forEach(el => {
          if (el) el.style.color = primary;
        });
      }
      
      // Update labels - do this separately to ensure we get fresh elements
      const featureLabels = document.querySelectorAll('.feature-image-label');
      if (featureLabels && featureLabels.length) {
        featureLabels.forEach(el => {
          if (el) el.style.backgroundColor = primary;
        });
      }
      
      // Update content borders
      if (domCache.colorContents && domCache.colorContents.length) {
        domCache.colorContents.forEach(el => {
          if (el) el.style.borderLeftColor = primary;
        });
      }
    });
  }

  // Update text based on color descriptions
  function updateColorSpecificContent(colorKey) {
    // Get it fresh each time
    const colorDescription = document.getElementById('color-specific-description');
    
    if (!colorDescription) return;
    
    const desc = apexColorManager.colorDescriptions[colorKey];
    if (!desc) return;
    
    requestAnimationFrame(() => {
      colorDescription.textContent = desc;
      
      // Skip animation on mobile for better performance
      if (!isMobile) {
        colorDescription.classList.add('color-description-update');
        setTimeout(() => colorDescription.classList.remove('color-description-update'), 500);
      }
    });
  }

  // Mark the current active slide
  function setActiveSlideState(sliderInstance) {
    if (!sliderInstance || !sliderInstance.Components || !sliderInstance.Components.Slides) return;
    
    // Get current visible slide (not cloned)
    const visibleIndex = sliderInstance.index;
    const activeSlide = sliderInstance.Components.Slides.getAt(visibleIndex)?.slide;
    
    if (!activeSlide) return;
    
    // Remove active class from all slides first
    document.querySelectorAll('.splide__slide').forEach(slide => {
      slide.classList.remove('active-color');
    });
    
    // Add active class to the current active slide
    activeSlide.classList.add('active-color');
  }

  // Fallback image error handler
  function handleImageError(e) {
    const src = e.target.src;
    if (!src.includes('fallback')) {
      if (src.includes('herodesk')) e.target.src = "{{ 'fallback-hero.webp' | asset_url }}";
      else if (src.includes('heromobile')) e.target.src = "{{ 'fallback-heromobile.webp' | asset_url }}";
      else if (src.includes('chestpiece')) e.target.src = "{{ 'fallback-chestpiece.webp' | asset_url }}";
      else if (src.includes('warranty')) e.target.src = "{{ 'fallback-warranty.webp' | asset_url }}";
      else if (src.includes('tube')) e.target.src = "{{ 'fallback-tube.webp' | asset_url }}";
      else if (src.includes('footerart')) e.target.src = "{{ 'fallback-footerart.webp' | asset_url }}";
    }
  }

  // Attach error handlers to images
  function setupImageErrorHandlers() {
    document.querySelectorAll('img').forEach(img => {
      img.addEventListener('error', handleImageError, { passive: true });
    });
  }

  // Initialize hero images for proper display
  function initHeroImages() {
    // Get all hero images
    const heroImages = document.querySelectorAll('.hero-picture');
    
    if (!heroImages || !heroImages.length) {
      console.log('No hero images found');
      return;
    }
    
    console.log(`Found ${heroImages.length} hero images to initialize`);
    
    // Process each image
    heroImages.forEach(img => {
      // Make sure only skyblue images are visible initially
      const isSkyblue = img.classList.contains('skyblue') || img.dataset.color === 'skyblue';
      
      img.style.display = isSkyblue ? 'block' : 'none';
      img.style.opacity = isSkyblue ? '1' : '0';
      
      // Ensure each image has a data-color attribute for consistency
      if (!img.dataset.color) {
        // Extract color name from class list
        const colorClass = Array.from(img.classList).find(c => 
          ['skyblue', 'coralbloom', 'gladegreen', 'midnightindigo', 
           'knockoutpink', 'endlessorange', 'tidalteal', 'timelessblack'].includes(c)
        );
        
        if (colorClass) {
          img.dataset.color = colorClass;
        }
      }
    });
  }

  // REVISED: Hero image color update function - simplified to match multirow and footer approach
  apexColorManager.changeHeroColor = function(colorKey) {
    console.log(`🔄 Updating hero color to: ${colorKey}`);
    
    // Get fresh hero images each time to ensure we have the latest DOM state
    const heroImages = document.querySelectorAll('.hero-picture');
    
    console.log(`Found ${heroImages.length} hero images`);
    
    requestAnimationFrame(() => {
      if (heroImages && heroImages.length) {
        heroImages.forEach(img => {
          // Use the same approach as multirow and footer - check class or data-color
          const isActive = img.classList.contains(colorKey) || img.dataset.color === colorKey;
          
          // Set opacity based on active color
          img.style.opacity = isActive ? '1' : '0';
          
          // Control display to match opacity
          if (isActive) {
            img.style.display = 'block';
          } else {
            // Delay hiding to allow for opacity transition
            setTimeout(() => {
              if (!img.classList.contains(colorKey) && img.dataset.color !== colorKey) {
                img.style.display = 'none';
              }
            }, 500);
          }
        });
      }
    });
  };

  // Multirow images update function
  apexColorManager.updateMultirowImages = function(colorKey) {
    // Get fresh elements each time
    const productFeatures = document.querySelectorAll('.product-feature-image');
    const featureLabels = document.querySelectorAll('.feature-image-label');
    const primaryColor = apexColorManager.colorMap[colorKey] || '#2B9CD6';
    
    requestAnimationFrame(() => {
      // Update product feature images
      if (productFeatures && productFeatures.length) {
        productFeatures.forEach(img => {
          const isActive = img.classList.contains(colorKey) || img.dataset.color === colorKey;
          
          // Set opacity based on active color
          img.style.opacity = isActive ? '1' : '0';
          img.style.zIndex = isActive ? '1' : '0';
          
          // Load the image if using data-src
          if (isActive && img.getAttribute('src') !== img.getAttribute('data-src') && img.getAttribute('data-src')) {
            img.setAttribute('src', img.getAttribute('data-src'));
          }
        });
      }
      
      // Update feature image labels with fresh color
      if (featureLabels && featureLabels.length) {
        featureLabels.forEach(label => {
          if (label) {
            label.style.backgroundColor = primaryColor;
          }
        });
      }
      
      // Update multirow captions and buttons
      updateMultirowColors(colorKey, primaryColor);
    });
  };

  // Footer artwork update function
  apexColorManager.updateFooterArtwork = function(colorKey) {
    // Get fresh elements each time
    const footerArtworks = document.querySelectorAll('.footer-artwork');
    
    requestAnimationFrame(() => {
      if (footerArtworks && footerArtworks.length) {
        footerArtworks.forEach(img => {
          img.style.opacity = (img.classList.contains(colorKey) || 
                              img.dataset.color === colorKey) ? '1' : '0';
        });
      }
    });
  };

  // Initialize the Splide carousel
  function initSplide() {
    if (typeof Splide === 'undefined') {
      console.error('❌ Splide not found');
      return;
    }
    
    // Mobile-optimized options
    const sliderOptions = isMobile ? {
      perPage: 3,
      type: 'loop',
      focus: 'center',
      arrows: false,
      pagination: false,
      updateOnMove: true,
      gap: 1.2,
      lazyLoad: 'nearby',
      breakpoints: {
        480: { perPage: 3, gap: 1 }
      }
    } : {
      // Desktop options
      perPage: 7,
      type: 'loop',
      focus: 'center',
      gap: 5,
      lazyLoad: 'nearby',
      breakpoints: {
        1200: { perPage: 5, gap: 4 },
        768: { perPage: 3, gap: 3 }
      }
    };
    
    // Make sure the carousel exists
    const carouselElement = document.getElementById('featured-product-carousel');
    if (!carouselElement) {
      console.log('Carousel element not found in this page');
      return null;
    }
    
    const slider = new Splide('#featured-product-carousel', sliderOptions).mount();

    // Mark carousel as visible since it's now initialized
    carouselElement.classList.add('is-initialized');

    // Navigation arrows with passive event listeners for better performance
    const nextButton = document.querySelector('.next-splide');
    const prevButton = document.querySelector('.prev-splide');
    
    if (nextButton) {
      nextButton.addEventListener('click', () => slider.go('>'), { passive: true });
    }
    
    if (prevButton) {
      prevButton.addEventListener('click', () => slider.go('<'), { passive: true });
    }

    // Splide event handlers
    slider.on('moved', () => {
      setTimeout(() => {
        setActiveSlideState(slider);
        updateColorScheme();
      }, 0);
    });
    
    slider.on('mounted', () => {
      setActiveSlideState(slider);
      updateColorScheme();
      fixClonedSlides();
    });
    
    // Original slide click behavior from your first version to maintain hyperlink function
    const originalSlides = document.querySelectorAll('.splide__slide:not(.is-cloned)');
    originalSlides.forEach(function(slide, index) {
      slide.addEventListener('click', function(e) {
        // Don't interfere with link clicks
        if (e.target.tagName.toLowerCase() === 'a' || e.target.closest('a')) {
          return; // Let the browser handle links normally
        }
        
        e.preventDefault();
        
        // First, ensure the right slide is set as active
        document.querySelectorAll('.splide__slide').forEach(s => s.classList.remove('active-color'));
        slide.classList.add('active-color');
        
        // Then update color scheme
        updateColorScheme();
        
        // Finally, move the slider (this will trigger the 'moved' event)
        slider.go(index);
      });
    });
    
    // Cloned slide click behavior - needs to find original slide
    const clonedSlides = document.querySelectorAll('.splide__slide.is-cloned');
    clonedSlides.forEach(function(clonedSlide) {
      clonedSlide.addEventListener('click', function(e) {
        // Don't interfere with link clicks
        if (e.target.tagName.toLowerCase() === 'a' || e.target.closest('a')) {
          return;
        }
        
        e.preventDefault();
        
        // Find the color of this cloned slide
        const colorKey = getColorKeyFromSlide(clonedSlide);
        
        // Find the original slide with the same color
        let targetIndex = -1;
        originalSlides.forEach(function(origSlide, idx) {
          if (getColorKeyFromSlide(origSlide) === colorKey) {
            targetIndex = idx;
          }
        });
        
        if (targetIndex !== -1) {
          slider.go(targetIndex);
        }
      });
    });
    
    return slider;
  }

  // Fix cloned slides that have placeholder images
  function fixClonedSlides() {
    const clonedSlides = document.querySelectorAll('.splide__slide.is-cloned');
    
    requestAnimationFrame(() => {
      clonedSlides.forEach(slide => {
        const img = slide.querySelector('img');
        if (!img) return;
        
        // Make sure image is visible
        img.style.opacity = '1';
        
        // If it's a placeholder, replace with real image
        if (img.src.includes('data:image')) {
          const colorKey = getColorKeyFromSlide(slide);
          
          const originalSlides = document.querySelectorAll('.splide__slide:not(.is-cloned)');
          let realSrc = null;
          
          for (let i = 0; i < originalSlides.length; i++) {
            const origColorKey = getColorKeyFromSlide(originalSlides[i]);
            
            if (origColorKey === colorKey) {
              const origImg = originalSlides[i].querySelector('img');
              if (origImg) {
                realSrc = origImg.dataset.src || origImg.src;
                if (realSrc && !realSrc.includes('data:image')) {
                  break;
                }
              }
            }
          }
          
          if (realSrc) {
            img.src = realSrc;
          }
        }
      });
    });
  }

  // Preload all carousel images
  function preloadCarouselImages() {
    requestAnimationFrame(() => {
      document.querySelectorAll('.splide__slide img').forEach(img => {
        img.style.opacity = '1';
        
        if (img.dataset.src && img.src !== img.dataset.src) {
          img.src = img.dataset.src;
        }
      });
    });
  }

  // Preload multirow images for immediate availability
  function preloadMultirowImages() {
    requestAnimationFrame(() => {
      // Force all skyblue product feature images to load
      const skyblueImages = document.querySelectorAll('.product-feature-image.skyblue');
      
      skyblueImages.forEach(img => {
        const imgSrc = img.getAttribute('data-src') || img.getAttribute('src');
        if (imgSrc && !imgSrc.includes('data:image')) {
          img.setAttribute('src', imgSrc);
          img.style.opacity = '1';
          img.style.zIndex = '1';
        }
      });
    });
  }

  // Fallback update handler - properly cleaned up
  function fallbackColorUpdate() {
    let colorKey = 'skyblue';
    const activeSlide = document.querySelector('.splide__slide.active-color');
    if (activeSlide) colorKey = getColorKeyFromSlide(activeSlide);

    updateColorClass(colorKey);

    if (typeof apexColorManager.changeHeroColor === 'function') {
      apexColorManager.changeHeroColor(colorKey);
    }
    if (typeof apexColorManager.updateMultirowImages === 'function') {
      apexColorManager.updateMultirowImages(colorKey);
    }
    if (typeof apexColorManager.updateFooterArtwork === 'function') {
      apexColorManager.updateFooterArtwork(colorKey);
    }

    const primaryColor = apexColorManager.colorMap[colorKey] || '#2B9CD6';
    const secondaryColor = apexColorManager.secondaryColorMap[colorKey] || '#2691C7';

    updateFeatureElements(primaryColor, secondaryColor);
    updateColorSpecificContent(colorKey);
    updateCTAButton(colorKey);
    updateMultirowColors(colorKey, primaryColor);
    updateBlogElements(primaryColor);
    updateFooterElements(primaryColor);

    // Set CSS variables at the :root level
    domCache.root.style.setProperty('--primary', primaryColor);
    domCache.root.style.setProperty('--secondary', secondaryColor);
    domCache.root.style.setProperty('--primary-color', primaryColor);
  }

  // Handle external color change events
  function handleColorChangeEvent(event) {
    if (!event.detail || !event.detail.color) return;
    
    const colorKey = event.detail.color;
    
    // Find matching slide by color key
    let matchingSlide = null;
    let matchingIndex = -1;
    const allSlides = Array.from(document.querySelectorAll('.splide__slide:not(.is-cloned)'));
    
    for (let i = 0; i < allSlides.length; i++) {
      const slideColorKey = getColorKeyFromSlide(allSlides[i]);
      if (slideColorKey === colorKey) {
        matchingSlide = allSlides[i];
        matchingIndex = i;
        break;
      }
    }
    
    if (matchingSlide && matchingIndex !== -1) {
      // Update slide classes
      document.querySelectorAll('.splide__slide').forEach(s => s.classList.remove('active-color'));
      matchingSlide.classList.add('active-color');
      
      // Go to slide if Splide exists
      if (typeof Splide !== 'undefined') {
        const splideInstance = Splide.getInstance('#featured-product-carousel');
        if (splideInstance) {
          splideInstance.go(matchingIndex);
        }
      }
      
      // Update color scheme
      updateColorScheme();
    } else {
      // If no matching slide (e.g., on product page), still update the color
      updateColorClass(colorKey);
      const primaryColor = apexColorManager.colorMap[colorKey] || '#2B9CD6';
      const secondaryColor = apexColorManager.secondaryColorMap[colorKey] || '#2691C7';
      
      // Set CSS variables at both :root and local levels
      domCache.root.style.setProperty('--primary', primaryColor);
      domCache.root.style.setProperty('--secondary', secondaryColor);
      domCache.root.style.setProperty('--primary-color', primaryColor);
      
      updateFeatureElements(primaryColor, secondaryColor);
      updateCTAButton(colorKey);
      updateMultirowColors(colorKey, primaryColor);
      updateBlogElements(primaryColor);
      updateFooterElements(primaryColor);
      
      if (typeof apexColorManager.changeHeroColor === 'function') {
        apexColorManager.changeHeroColor(colorKey);
      }
      
      if (typeof apexColorManager.updateMultirowImages === 'function') {
        apexColorManager.updateMultirowImages(colorKey);
      }
    }
  }

  // Initialize critical components - revised initialization sequence
  function init() {
    // 1. Cache DOM elements first for better performance
    cacheDOM();
    
    // 2. Initialize hero images for proper display
    initHeroImages();
    
    // 3. Set up image error handlers
    setupImageErrorHandlers();
    
    // 4. Set default color class and variables
    const defaultColor = 'skyblue';
    updateColorClass(defaultColor);
    
    // 5. Initial hero update
    if (typeof apexColorManager.changeHeroColor === 'function') {
      apexColorManager.changeHeroColor(defaultColor);
    }
    
    // 6. Set up other UI elements with default color
    const primaryColor = apexColorManager.colorMap[defaultColor];
    const secondaryColor = apexColorManager.secondaryColorMap[defaultColor];
    
    updateCTAButton(defaultColor);
    updateMultirowColors(defaultColor, primaryColor);
    updateBlogElements(primaryColor);
    updateFooterElements(primaryColor);
    updateFeatureElements(primaryColor, secondaryColor);
    
    // 7. Set global CSS variables
    domCache.root.style.setProperty('--primary', primaryColor);
    domCache.root.style.setProperty('--secondary', secondaryColor);
    domCache.root.style.setProperty('--primary-color', primaryColor);
    
    // 8. Listen for color change events
    document.addEventListener('apex:colorChanged', handleColorChangeEvent, { passive: true });
    
    // 9. Fallback update on document ready
    document.addEventListener('readystatechange', function() {
      if (document.readyState === 'complete') {
        setTimeout(fallbackColorUpdate, 1);
      }
    });
    
    // 10. Preload default images with a short delay
    setTimeout(preloadMultirowImages, 100);
    
    // 11. Preload carousel images when page is fully loaded
    if (document.readyState === 'complete') {
      preloadCarouselImages();
    } else {
      window.addEventListener('load', preloadCarouselImages, { passive: true });
    }
    
    // 12. Initialize Splide when browser is idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        const slider = initSplide();
        if (!slider) {
          // If Splide isn't available, still update the color scheme
          fallbackColorUpdate();
        }
      }, { timeout: 800 });
    } else {
      setTimeout(() => {
        const slider = initSplide();
        if (!slider) {
          // If Splide isn't available, still update the color scheme
          fallbackColorUpdate();
        }
      }, 400);
    }
  }
  
  // Start initialization
  init();
});